/**
 * 未提交的表单草稿（CR-037）。
 *
 * 客户原话：「填写的内容在拍照和视频录取后丢失，需要重新填写」。
 *
 * 根因不在表单里，在安卓：相机 App 一到前台，系统随时可以回收我们这个 Activity。
 * 拍完回来 WebView 是**重新加载**的 —— React 全部重挂，`useState` 里的东西
 * 一个字都不剩。这不是偶发，工地手机内存越紧张越必然。
 *
 * 所以三张表单（日报 / 隐蔽工程 / 采购）填到一半的内容必须落盘：
 * - 文字走 **localStorage**：同步可读，页面挂载时第一帧就能填回去，不闪空表；
 * - 照片和视频走 **IndexedDB**：Blob 放不进 localStorage。
 *
 * **和上传队列分开一个库**。队列里的东西是「工人已经按过提交、系统承诺会传上去」的，
 * 草稿是「他还在填」的 —— 两者的生命周期、清理时机、丢失的后果都不一样，
 * 混在一起迟早会有人写出一条把对方一起清掉的逻辑。
 *
 * **草稿按用户分**（key 里带 userId）：手机由公司统一发放、多人轮换（CR-006），
 * A 填了一半的采购不能在 B 登录后出现在 B 的表单里。退出登录时连草稿一起清掉。
 */

const DB_NAME = 'construction-drafts';
const DB_VERSION = 1;
const MEDIA = 'media';

const FIELDS_PREFIX = 'draft:';

/** 草稿的 key。三张表单各一个，再按用户隔离。 */
export type DraftKind = 'daily-log' | 'hidden-work' | 'purchase';

export function draftKey(kind: DraftKind, userId: number): string {
  return `${kind}:${userId}`;
}

export interface DraftMedia {
  id: string;
  draftKey: string;
  blob: Blob;
  mimeType: string;
  /** 拍摄 / 录制时刻（epoch ms）。提交隐蔽工程时要当作 capturedAt 上报 */
  capturedAt: number;
  isVideo: boolean;
  addedAt: number;
}

// ---------------------------------------------------------------- 文字部分

/**
 * 读回文字字段。**同步**，所以表单第一帧就能是恢复后的样子。
 *
 * 存进去的是什么类型，读出来就当什么类型用 —— 这里不做校验：
 * 草稿是我们自己写的，格式对不上说明代码改了，那时候旧草稿本来也该丢。
 * 只挡住「解析失败」这一种，避免一条脏数据让整个表单页崩掉。
 */
export function readFields<T>(key: string): Partial<T> | null {
  try {
    const raw = localStorage.getItem(FIELDS_PREFIX + key);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? (parsed as Partial<T>) : null;
  } catch {
    return null;
  }
}

export function writeFields(key: string, fields: Record<string, unknown>): void {
  try {
    localStorage.setItem(FIELDS_PREFIX + key, JSON.stringify(fields));
  } catch {
    // 存不下就退化成「这次不保存」。表单照样能用，不能因为存草稿失败挡住填表
  }
}

function removeFields(key: string): void {
  try {
    localStorage.removeItem(FIELDS_PREFIX + key);
  } catch {
    /* 同上 */
  }
}

// ---------------------------------------------------------------- 媒体部分

let dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;

  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(MEDIA)) {
        const store = db.createObjectStore(MEDIA, { keyPath: 'id' });
        store.createIndex('draftKey', 'draftKey');
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('IndexedDB 打开失败'));
    request.onblocked = () => reject(new Error('IndexedDB 被其他标签页锁住'));
  });

  return dbPromise;
}

function req<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('IndexedDB 操作失败'));
  });
}

function tx<T>(mode: IDBTransactionMode, run: (store: IDBObjectStore) => Promise<T> | T): Promise<T> {
  return openDb().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const transaction = db.transaction([MEDIA], mode);
        let result: T;
        transaction.oncomplete = () => resolve(result);
        transaction.onerror = () => reject(transaction.error ?? new Error('IndexedDB 事务失败'));
        transaction.onabort = () => reject(transaction.error ?? new Error('IndexedDB 事务中断'));
        Promise.resolve(run(transaction.objectStore(MEDIA)))
          .then((value) => {
            result = value;
          })
          .catch(reject);
      }),
  );
}

/**
 * 谁在看这份草稿。
 *
 * 被系统打断的那张照片是**在表单之外**恢复出来的（见 useCaptureRecovery），
 * 而且要重新解码、压缩，往往比表单挂载晚几百毫秒。没有这个通知，
 * 工人会看到一张「文字都在、照片没了」的表单，然后什么也不会发生。
 */
type DraftListener = (key: string) => void;
const listeners = new Set<DraftListener>();

export function onDraftChange(listener: DraftListener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function notify(key: string): void {
  for (const listener of listeners) {
    try {
      listener(key);
    } catch {
      // 一个订阅者出错不能影响别人，更不能影响写入本身
    }
  }
}

export async function listMedia(key: string): Promise<DraftMedia[]> {
  const rows = await tx('readonly', (store) =>
    req(store.index('draftKey').getAll(key) as IDBRequest<DraftMedia[]>),
  );
  return rows.sort((a, b) => a.addedAt - b.addedAt);
}

export async function addMedia(item: DraftMedia): Promise<void> {
  await tx('readwrite', (store) => req(store.put(item)));
  notify(item.draftKey);
}

export async function removeMedia(id: string): Promise<void> {
  const row = await tx('readonly', (store) =>
    req(store.get(id) as IDBRequest<DraftMedia | undefined>),
  );
  await tx('readwrite', (store) => req(store.delete(id)));
  if (row) notify(row.draftKey);
}

async function removeMediaOf(key: string): Promise<void> {
  await tx('readwrite', async (store) => {
    const keys = (await req(store.index('draftKey').getAllKeys(key))) as IDBValidKey[];
    for (const id of keys) store.delete(id);
  });
}

// ---------------------------------------------------------------- 清理

/** 提交成功后调用：文字和媒体一起清掉。 */
export async function clearDraft(key: string): Promise<void> {
  removeFields(key);
  try {
    await removeMediaOf(key);
  } catch {
    // 清不掉就留着，下次进这张表单会被当成「恢复出来的草稿」——
    // 比让提交流程因为清理失败而报错要好
  }
  notify(key);
}

/**
 * 退出登录时清掉这台手机上所有的草稿。
 *
 * 和「队列数据留在本地等本人下次登录接着传」相反：**草稿不是承诺**，
 * 工人没按提交，系统也没答应过要保住它。而共享手机上留着上一个人填了一半的
 * 采购金额和备注，是把他的东西给了下一个人看。
 */
export async function clearAllDrafts(): Promise<void> {
  try {
    for (const key of Object.keys(localStorage)) {
      if (key.startsWith(FIELDS_PREFIX)) localStorage.removeItem(key);
    }
  } catch {
    /* 清不掉就算了，不能挡住退出 */
  }

  try {
    await tx('readwrite', (store) => req(store.clear()));
  } catch {
    /* 同上 */
  }
}
